// Field Notes — dashboard surfaces (ledger rows 14-16).
// Ported from design/field-notes/dashboard/surfaces.jsx. Purely
// presentational: props in, JSX out; data shaping stays in the pages.
// Every concept row/cell is a "talk to the tutor about this" target.
import React from 'react';
import { MasteryGlyph, DueMarker, BlockerMarker } from './mastery';
import './dashboard.css';

const LV_VAR = {
  novice: 'var(--m-novice)', struggling: 'var(--m-struggling)', developing: 'var(--m-developing)',
  proficient: 'var(--m-proficient)', advanced: 'var(--m-advanced)', mastered: 'var(--m-mastered)',
};
export const LV_ORDER = ['novice', 'struggling', 'developing', 'proficient', 'advanced', 'mastered'];

/* stacked distribution bar — "memory made visible" at course/domain scale */
export function DistributionBar({ dist, total, size, legend }) {
  const sum = total || LV_ORDER.reduce((s, l) => s + (dist[l] || 0), 0) || 1;
  return (
    <div>
      <div className={'distbar' + (size === 'lg' ? ' lg' : '')} role="img"
           aria-label={LV_ORDER.map((l) => (dist[l] || 0) + ' ' + l).join(', ')}>
        {LV_ORDER.map((l) => (dist[l]
          ? <i key={l} style={{ width: (dist[l] / sum * 100) + '%', backgroundColor: LV_VAR[l] }} />
          : null))}
      </div>
      {legend && (
        <div className="distlegend">
          {LV_ORDER.filter((l) => dist[l]).map((l) => (
            <span className="dl" key={l}>
              <span className="sw" style={{ backgroundColor: LV_VAR[l] }} />
              <b>{dist[l]}</b> {l}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* concept row — the whole row is a tutor-gravity target */
export function ConceptRow({ m, isBlocker, onAsk }) {
  return (
    <div className="concept-row" role="button" tabIndex={0}
         onClick={() => onAsk(m, isBlocker)}
         onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAsk(m, isBlocker); } }}>
      <MasteryGlyph level={m.mastery_level} />
      <span className="cr-name">{m.concept}</span>
      <span className="cr-prof">{m.mastery_level}</span>
      <span className="cr-markers">
        {isBlocker && <span className="cr-blocker-tag"><BlockerMarker /> blocker</span>}
        {m.due_for_review && <span className="cr-due"><DueMarker /> due</span>}
        <span className="cr-ask">Ask the tutor →</span>
      </span>
    </div>
  );
}

/* concept cell — dense grid variant (quiet at 24+) */
export function ConceptCell({ m, onAsk }) {
  return (
    <div className={'concept-cell' + (m.is_blocker ? ' is-blocker' : '')} role="button" tabIndex={0}
         onClick={() => onAsk(m, m.is_blocker)}
         onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAsk(m, m.is_blocker); } }}
         title={'Ask the tutor about ' + m.concept}>
      <MasteryGlyph level={m.mastery_level} />
      <div className="ccx-body">
        <div className="ccx-name">{m.concept}</div>
        <div className="ccx-prof">
          {m.mastery_level}
          {typeof m.mastery_score === 'number' && <span> · {m.mastery_score.toFixed(1)}</span>}
        </div>
      </div>
      <span className="ccx-marks">
        {m.is_blocker && <BlockerMarker />}
        {m.due_for_review && <DueMarker />}
      </span>
    </div>
  );
}

/* course card — library grid. `dist` optional: renders the unfilled
   track until the per-course summary endpoint ships. */
export function CourseCard({ c, onOpen }) {
  const fresh = !c.dist;
  return (
    <div className="course-card" role="button" tabIndex={0} onClick={() => onOpen(c)}
         onKeyDown={(e) => { if (e.key === 'Enter') onOpen(c); }}>
      <div className="cc-top">
        <span className={'cc-status ' + (c.status || 'active')} />
        <span className="cc-code">{c.code}</span>
        {c.blockers > 0 && <span className="cc-blockers"><BlockerMarker /> {c.blockers}</span>}
      </div>
      <div className="cc-name">{c.name}</div>
      <div className="cc-desc">{c.description}</div>
      <div className="cc-spacer" />
      <div className="cc-dist">
        {fresh
          ? <div className="distbar" aria-label="not started"><i style={{ width: '100%', backgroundColor: 'var(--m-track)' }} /></div>
          : <DistributionBar dist={c.dist} total={c.concepts} />}
      </div>
      <div className="cc-foot">
        <span>{c.concepts} concepts</span>
        <span className="sep">·</span>
        {c.due > 0 ? <span className="due"><DueMarker /> {c.due} due</span> : <span>{fresh ? 'not started' : 'caught up'}</span>}
        {c.last_active && <span className="last">{c.last_active}</span>}
      </div>
    </div>
  );
}

/* domain card — dashboard section: head (name/desc/dist/due) + concept rows */
export function DomainCard({ d, onAsk, footer }) {
  return (
    <div className="domain-card">
      <div className="domain-card-head">
        <div className="dh-main">
          <div className="dh-name">{d.name}</div>
          {d.description && <div className="dh-desc">{d.description}</div>}
        </div>
        {d.rows.length > 0 && <div className="dh-dist"><DistributionBar dist={d.dist} total={d.rows.length} /></div>}
        {d.due > 0 && <span className="dh-due"><DueMarker /> {d.due}</span>}
      </div>
      {d.rows.map((m) => (
        <ConceptRow key={m.concept} m={m} isBlocker={(d.blockers || []).includes(m.concept)} onAsk={onAsk} />
      ))}
      {footer}
    </div>
  );
}
